import { useMemo } from "react";

const useProposalCalculator = (sections, options = {}) => {
  const {
    lineItemDiscount = true,
    oneLineDiscount = false,
    oneLineDiscountValue = 0,
    oneLineDiscountType = "%",
  } = options;

  // Helper function to round to 2 decimals
  const roundToTwo = num => Math.round((num + Number.EPSILON) * 100) / 100;

  // Conversion function
  const convertTaxStrings = (taxStrings = []) => {
    return taxStrings?.map(taxStr => {
      // Extract the rate number (including decimal points if needed)
      const rateMatch = taxStr.match(/(\d+(\.\d+)?)%?$/);
      const rate = rateMatch ? parseFloat(rateMatch[1]) : 0;

      return {
        name: taxStr,
        rate: rate,
      };
    });
  };

  // Calculate item discount
  const calculateDiscount = item => {
    if (!lineItemDiscount || !item.data.discount) return 0;

    const subtotal = item.data?.unit_price * (item.data?.quantity || 0);
    let discount = 0;

    if (typeof item.data.discountType === "string" && item.data.discountType.includes("%")) {
      const percent = parseFloat(item.data.discount) / 100;
      discount = subtotal * percent;
    } else {
      discount = parseFloat(item.data.discount);
    }

    return roundToTwo(discount);
  };

  // Calculate item amount with taxes
  const calculateItemAmount = item => {
    if (!item.data?.item_name) return 0; // Skip if no item_name

    const subtotal = roundToTwo(item.data.unit_price * (item.data.quantity || 0));
    const discount = calculateDiscount(item);
    const amount = roundToTwo(subtotal - discount);

    return amount;
  };

  // Calculate section total
  const calculateSectionTotal = section => {
    const total = section.items.reduce((sum, item) => {
      if (!item.data?.item_name || item.active === false) return sum;
      return sum + calculateItemAmount(item);
    }, 0);
    return roundToTwo(total);
  };

  // Calculate all taxes across all items
  const calculateDetailedTaxes = (sections, oneLineDiscountAmount = 0, grandSubtotal = 0) => {
    // Step 1: Get all unique tax names (case-insensitive)
    const taxMap = new Map(); // { lowercaseName: { originalName, rate } }

    sections?.forEach(group => {
      // Skip inactive sections
      if (group.active === false) return;

      group.items.forEach(item => {
        // Skip inactive items
        if (item.active === false) return;

        item.tax.forEach(tax => {
          if (!tax.name) return;
          const lowerName = tax.name.toLowerCase();
          if (!taxMap.has(lowerName)) {
            taxMap.set(lowerName, { originalName: tax.name, rate: tax.rate });
          }
        });
      });
    });

    // Step 2: Calculate taxable amount for each tax
    const result = Array.from(taxMap.values()).map(tax => {
      let totalTaxableAmount = 0;

      sections?.forEach(group => {
        // Skip inactive sections
        if (group.active === false) return;

        group.items.forEach(item => {
          // Skip inactive items
          if (item.active === false) return;

          const matchingTax = item.tax.find(t => t.name.toLowerCase() === tax.originalName.toLowerCase());
          if (matchingTax) {
            totalTaxableAmount += item.amount * (matchingTax.rate / 100);
          }
        });
      });

      // Apply discount adjustment
      const discountFactor = grandSubtotal === 0 ? 0 : 1 - oneLineDiscountAmount / grandSubtotal;
      const adjustedAmount = roundToTwo(totalTaxableAmount * discountFactor);

      return {
        name: tax.originalName,
        amount: adjustedAmount,
      };
    });

    return result;
  };

  // Main calculation function
  const calculate = () => {
    // Calculate section totals and item amounts
    const sectionsWithTotals = sections?.map(section => {
      // Calculate amounts for ALL items (including inactive ones)
      const itemsWithAmounts = section?.items?.map(item => {
        const amount = calculateItemAmount(item);
        const tax = item.active === false ? [] : convertTaxStrings(item.data?.tax || []);
        return { ...item, amount, tax };
      });

      // Calculate section total (only from active items)
      const sectionTotal = roundToTwo(
        itemsWithAmounts.reduce((sum, item) => {
          return item.active === false ? sum : sum + item.amount;
        }, 0)
      );

      return {
        ...section,
        total: sectionTotal,
        items: itemsWithAmounts,
      };
    });

    // Calculate grand subtotal (sum of all active section totals)
    const grandSubtotal =
      roundToTwo(
        sectionsWithTotals?.reduce((sum, section) => {
          return section.active === false ? sum : sum + section.total;
        }, 0)
      ) || 0;

    // Calculate one-line discount
    const oneLineDiscountAmount =
      roundToTwo(
        oneLineDiscount
          ? oneLineDiscountType === "%"
            ? grandSubtotal * (oneLineDiscountValue / 100)
            : parseFloat(oneLineDiscountValue)
          : 0
      ) || 0;

    // Calculate detailed taxes (only from active sections and items)
    const detailedTaxes = calculateDetailedTaxes(sectionsWithTotals, oneLineDiscountAmount, grandSubtotal) || [];
    const totalTaxAmount = roundToTwo(detailedTaxes.reduce((sum, tax) => sum + tax.amount, 0)) || 0;

    // Calculate grand total
    const grandTotal = roundToTwo(grandSubtotal + totalTaxAmount - oneLineDiscountAmount) || 0;

    return useMemo(
      () => ({
        sections: sectionsWithTotals,
        grandSubtotal,
        oneLineDiscountAmount,
        detailedTaxes,
        totalTaxAmount,
        grandTotal,
      }),
      [sectionsWithTotals, grandSubtotal, oneLineDiscountAmount, detailedTaxes, totalTaxAmount, grandTotal]
    );
  };
  return calculate();
};

export default useProposalCalculator;
